import React, { useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import StructuredData from './StructuredData/StructuredData';
import { getOrganizationSchema } from '@/constants/pageMetadata';
import { getFullUrl } from '@/lib/utils/url';

interface DocumentHeadProps {
  title?: string;
  description?: string;
  noindex?: boolean;
}

/**
 * Manages document head elements (title, description, canonical, robots).
 * Uses useEffect for DOM manipulation to avoid React 19's metadata hoisting
 * issues with client-side routing.
 */
export const DocumentHead: React.FC<DocumentHeadProps> = ({ title, description, noindex }) => {
  const location = useLocation();

  // Calculate canonical URL: strip non-content params (e.g. theme) and trailing slashes.
  const canonicalHref = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('theme');
    const search = searchParams.toString();
    const path = `${location.pathname}${search ? `?${search}` : ''}`;
    return getFullUrl(path);
  }, [location.pathname, location.search]);

  // Manage all head elements imperatively
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return; // Skip during SSR
    }

    // Update document title
    if (title) {
      document.title = title;
    }

    // Use data attributes to identify our elements (safer than refs)
    const DATA_ATTR = 'data-document-head-managed';

    // Manage description meta tag
    if (description) {
      let descMeta = document.querySelector(
        `head > meta[name="description"][${DATA_ATTR}]`
      ) as HTMLMetaElement;
      if (!descMeta) {
        // Check if there's an existing one without our attribute
        descMeta = document.querySelector('head > meta[name="description"]') as HTMLMetaElement;
      }
      if (descMeta) {
        descMeta.content = description;
        descMeta.setAttribute(DATA_ATTR, 'true');
      } else {
        descMeta = document.createElement('meta');
        descMeta.name = 'description';
        descMeta.content = description;
        descMeta.setAttribute(DATA_ATTR, 'true');
        document.head.appendChild(descMeta);
      }
    }

    // Manage robots meta tag
    let robotsMeta = document.querySelector(
      `head > meta[name="robots"][${DATA_ATTR}]`
    ) as HTMLMetaElement;
    if (!robotsMeta) {
      robotsMeta = document.querySelector('head > meta[name="robots"]') as HTMLMetaElement;
    }
    if (robotsMeta) {
      robotsMeta.content = noindex ? 'noindex, nofollow' : 'index, follow';
      robotsMeta.setAttribute(DATA_ATTR, 'true');
    } else {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      robotsMeta.content = noindex ? 'noindex, nofollow' : 'index, follow';
      robotsMeta.setAttribute(DATA_ATTR, 'true');
      document.head.appendChild(robotsMeta);
    }

    // Manage canonical link
    let canonicalLink = document.querySelector(
      `head > link[rel="canonical"][${DATA_ATTR}]`
    ) as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.querySelector('head > link[rel="canonical"]') as HTMLLinkElement;
    }
    if (canonicalLink) {
      canonicalLink.href = canonicalHref;
      canonicalLink.setAttribute(DATA_ATTR, 'true');
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = canonicalHref;
      canonicalLink.setAttribute(DATA_ATTR, 'true');
      document.head.appendChild(canonicalLink);
    }

    // No cleanup - let React Router manage head elements
    // We just update them in place
  }, [title, description, noindex, canonicalHref]);

  // Render nothing - head elements are managed imperatively
  return null;
};

/**
 * Provider component that sets up default head elements and organization schema.
 */
export const DocumentHeadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const robotsRef = useRef<HTMLMetaElement | null>(null);

  // Set up default robots meta tag
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return; // Skip during SSR
    }

    // Only add default if there isn't one already (child DocumentHead will override)
    if (!document.querySelector('head > meta[name="robots"]')) {
      const robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      robotsMeta.content = 'index, follow';
      document.head.appendChild(robotsMeta);
      robotsRef.current = robotsMeta;
    }

    return () => {
      // Don't remove - let React Router manage head elements
      robotsRef.current = null;
    };
  }, []);

  return (
    <>
      <StructuredData data={getOrganizationSchema()} />
      {children}
    </>
  );
};
